/* React */
import React from "react";

/* Ionic */
import { IonButton } from "@ionic/react";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import { setAnswerLanguage } from "src/redux/features/agent-chat/agentChatSlice";

/* Interfaces */
import {
  AGENT_ANSWER_LANGUAGES,
  AGENT_ANSWER_LANGUAGE_LABELS,
  AgentAnswerLanguage,
} from "src/shared/interfaces/api/AgentSpec";

/* Stylesheet */
import styles from "./LanguageSelect.module.scss";

/**
 * The language the assistant asks its questions in.
 *
 * A choice rather than something inferred from the prompt. Both
 * attempts at inferring it were worse than not trying: a
 * German-speaking deployment answered English prompts in German,
 * and once each in French and Russian. Two words carry too little
 * to go on, and a browser locale is always present, so it drowned
 * the prompt out instead of backing it up.
 *
 * Two buttons rather than a dropdown, because there are two
 * choices and the current one should be visible without opening
 * anything. Always rendered, unlike the model picker: a setting
 * nobody can see is a setting nobody uses.
 */
const LanguageSelect: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { answerLanguage, isAnswering } = useSelector(
    (state: RootState) => state.agentChat
  );

  return (
    <div
      data-testid="language-select"
      className={styles.language_select}
    >
      {AGENT_ANSWER_LANGUAGES.map(
        (language: AgentAnswerLanguage) => (
          <IonButton
            key={language}
            size="small"
            fill={
              answerLanguage === language
                ? "solid"
                : "outline"
            }
            data-testid={`language-option-${language}`}
            aria-pressed={answerLanguage === language}
            aria-label={`Ask questions in ${AGENT_ANSWER_LANGUAGE_LABELS[language]}`}
            disabled={isAnswering}
            onClick={() =>
              dispatch(setAnswerLanguage(language))
            }
          >
            {language.toUpperCase()}
          </IonButton>
        )
      )}
    </div>
  );
};

export default LanguageSelect;
